import { appConfig } from "@common/config/app.config";
import { Subscription } from "../../bt-common/schemas/bt/subscription";
import { UserInterface } from "@common/models/user";
import moment, { Moment } from "moment";
import randomstring from "randomstring";
import { crispService } from "./crisp-service";
import { getStableIds, isFingerprintSupported } from "./fingerprint.service";
import { fingerprintToUuid } from "./fingerprintToUuid";
interface MeasurementProtocolItem {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price?: number;
    quantity?: number;
}
import { getStorageKey, setStorageKey } from "./storage";
import { VznLytics } from "./vznlytics";
import { readAttributionCookie } from "@common/utils/attributionReader";
import { readAttributionData, storeAttributionData, getGaSessionId, getGaClientId, isValidGaClientId } from "@common/utils/attribution";
import { sendMetaEvent } from "./meta-pixel-dispatch";
import { trackSignup } from "./facebook-conversions";
import { fireRegistrationOnce, KeyValueStore } from "./registration-event";
import { isExtensionContext } from "@common/services/detect-context";
import { consentAllows } from "@common/services/consent/gate";
import { hasAnalyticsConsent, hasMarketingConsent } from "@common/services/consent/store";
import { onConsentChange } from "@common/services/consent/loaders";

/**
 * Get the most recent click ID from an array of click IDs
 */
function getLatestClickId(clickIds?: string[]): string | undefined {
  return clickIds && clickIds.length > 0
    ? clickIds[clickIds.length - 1]
    : undefined;
}

/**
 * Extract attribution parameters for GA4 event tracking
 *
 * Sources (in priority order):
 * 1. blueticks_attribution cookie (available on .blueticks.co domains)
 * 2. user.analytics_data from backend (fallback for extension on web.whatsapp.com)
 *
 * IMPORTANT: Uses custom dimension names (first_touch_*) to avoid conflicts with GA4's
 * automatic session attribution. GA4 tracks sessionSource/sessionMedium from URL params.
 * Our custom dimensions track first-touch attribution separately.
 */
function getAttributionParams(eventName?: string, userAnalyticsData?: any): Record<string, string> {
  const params: Record<string, string> = {};
  // Attribution (UTM, click IDs) is marketing data: nothing is attached without marketing consent.
  if (!consentAllows("marketing")) return params;

  try {
    // Try cookie first, fall back to user's stored analytics_data (for extension context)
    const attribution = readAttributionCookie() || buildAttributionFromUserData(userAnalyticsData);
    if (!attribution) return params;

    // List of conversion events where we want to send click IDs
    const conversionEvents = [
      'register_new_user',
      'google_register_new_user',
      'signup_completed',
      'login',
      'purchase',
      'subscription_created',
      'trial_started',
      'first_schedule_created',
      'first_campaign_created'
    ];

    const isConversionEvent = eventName && conversionEvents.includes(eventName);

    // Only send click IDs on conversion events to avoid polluting regular event data
    if (isConversionEvent) {
      const clickIdMap = {
        gclid: attribution.conversion_ids?.gclids,
        fbclid: attribution.conversion_ids?.fbclids,
        msclkid: attribution.conversion_ids?.msclkids,
        ttclid: attribution.conversion_ids?.ttclids,
      };

      Object.entries(clickIdMap).forEach(([key, ids]) => {
        const latestId = getLatestClickId(ids);
        if (latestId) params[key] = latestId;
      });
    }

    // Add first-touch UTM parameters as CUSTOM dimensions (prefixed to avoid GA4 conflicts)
    // These will be registered as custom dimensions in GA4 admin console
    if (attribution.first_touch) {
      const utmMap = {
        first_touch_source: attribution.first_touch.utm_source,
        first_touch_medium: attribution.first_touch.utm_medium,
        first_touch_campaign: attribution.first_touch.utm_campaign,
        first_touch_content: attribution.first_touch.utm_content,
        first_touch_term: attribution.first_touch.utm_term,
      };

      Object.entries(utmMap).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
  } catch (error) {
    console.warn('[Analytics] Error reading attribution data:', error);
  }

  return params;
}

/**
 * Build AttributionData from user.analytics_data stored in the backend.
 * Used as fallback when the blueticks_attribution cookie is not accessible
 * (e.g., extension running on web.whatsapp.com).
 */
function buildAttributionFromUserData(analyticsData: any): import("@common/utils/attributionReader").AttributionData | null {
  if (!analyticsData) return null;

  // analytics_data already has the nested format (first_touch, conversion_ids)
  if (analyticsData.first_touch || analyticsData.conversion_ids) {
    return {
      first_touch: analyticsData.first_touch || {},
      conversion_ids: analyticsData.conversion_ids || { gclids: [], fbclids: [], msclkids: [], ttclids: [] },
      ga_client_id: analyticsData.ga_client_id || null,
      last_updated: analyticsData.last_updated || new Date().toISOString(),
    };
  }

  // Legacy flat format from older user records
  if (analyticsData.utm_source || analyticsData.gclid) {
    return {
      first_touch: {
        utm_source: analyticsData.utm_source || null,
        utm_medium: analyticsData.utm_medium || null,
        utm_campaign: analyticsData.utm_campaign || null,
        utm_content: analyticsData.utm_content || null,
        utm_term: analyticsData.utm_term || null,
        landing_page: analyticsData.landing_page || null,
        timestamp: analyticsData.timestamp || new Date().toISOString(),
        page_url: analyticsData.page_url || '',
        referrer: analyticsData.referrer || '',
      },
      conversion_ids: {
        gclids: analyticsData.gclid ? [analyticsData.gclid] : [],
        fbclids: analyticsData.fbclid ? [analyticsData.fbclid] : [],
        msclkids: analyticsData.msclkid ? [analyticsData.msclkid] : [],
        ttclids: analyticsData.ttclid ? [analyticsData.ttclid] : [],
      },
      ga_client_id: analyticsData.ga_client_id || null,
      last_updated: new Date().toISOString(),
    };
  }

  return null;
}

let _instance: any;

export interface EventOptions {
  category?: string;
  action?: string;
  label?: string;
  value?: any;
  [key: string]: any;
}

export interface TransactionData {
  transaction_id?: string;
  affiliation?: string;
  value?: number;
  coupon?: string;
  currency?: string;
  items: MeasurementProtocolItem[]
}

export interface VznlyticsEvent {
  event_name: string;
  triggered_by?: "click" | "view" | "scroll" | "server" | "gateway";
  value?: any;
  data?: any;
  label?: string;
  category?: string;
  sub_category?: string;
  entity_id?: string;
  entity_display_name?: string;
  entity_type?: string;
  email?: string;
  analytics_data?: {
    mauticId?: string;
    [key: string]: any;
  };
}

export class AnalyticsService {
  cid?: string;
  stableVisitorId?: string;
  ga4ClientId?: string;
  deviceCoreId?: string;
  userId?: string; // User ID from system when logged in
  ga4SessionId?: string; // Native GA4 session_id for server-side event linking
  fbqLoaded: any;
  bizsdk: any;
  bizsdkUserData: any;
  vznLytics: VznLytics = new VznLytics();
  user?: UserInterface;
  initPromise?: Promise<void>;
  // Invariant: singleton constructor assigns sessionID on first construction; later calls return existing _instance.
  sessionID!: string;
  // Invariant: singleton constructor assigns startTime on first construction; later calls return existing _instance.
  startTime!: Moment;

  // Event queue for web app context: holds events until gtag loads
  private gtagEventQueue: Array<{ type: 'event' | 'set' | 'set_user_properties'; args: any[] }> = [];

  /**
   * Whether we're in a web app context (not extension).
   * Uses isExtensionContext() for definitive detection instead of checking gtag availability,
   * which has a race condition on page load.
   */
  get isWebAppContext() {
    return !isExtensionContext();
  }

  constructor() {
    if (!_instance) {
      _instance = this;
      _instance.sessionID = randomstring.generate(16);
      _instance.startTime = moment();
      _instance.initPromise = _instance.initializeIdentifiers();
    }

    return _instance;
  }

  get hasGTAG() {
    return typeof window === "object" && window.gtag;
  }


  get hasFBQ() {
    return typeof window === "object" && window.fbq;
  }



  defaultParams() {
    const params: any = {
      engagement_time_msec: moment().diff(moment(this.startTime), 'milliseconds'),
      session_id: this.sessionID,
      // Do NOT send client_id in event params — GA4's gtag.js treats it as reserved
      // and will override the _ga cookie, breaking native session management.
      // GA4 manages its own client_id via the _ga cookie.
    };

    // Add user ID if available
    if (this.userId) {
      params.user_id = this.userId;
    }

    // Add stable IDs if available
    if (this.stableVisitorId) {
      params.stable_visitor_id = this.stableVisitorId;
    }
    if (this.deviceCoreId) {
      params.device_core_id = this.deviceCoreId;
    }

    // Add fingerprint data if available (backward compatibility)
    if (this.deviceCoreId) {
      params.fingerprint_id = this.deviceCoreId;
    }

    // Add attribution data (first-touch UTM parameters only, no click IDs)
    // Click IDs are only added for specific conversion events in event() method
    // Falls back to user.analytics_data when cookie unavailable (extension context)
    Object.assign(params, getAttributionParams(undefined, this.user?.analytics_data));

    return params;
  }

  async initializeIdentifiers() {
    if (consentAllows("analytics")) {
      await this.setStableIds();
      await this.setUid();
    } else {
      // Fingerprint and stable IDs wait for analytics consent (spec section 5 step 4).
      this.awaitAnalyticsConsent();
    }
    // Store GA4's native client_id (from _ga cookie) in the attribution cookie.
    // This is needed so server-side MP events (purchase, extension signup) can match
    // the browser's GA4 user. We no longer override GA4's client_id with fingerprint.
    this.syncGA4ClientIdToAttribution();
    // Capture GA4 native session_id for server-side event linking
    this.ga4SessionId = await this.getGA4SessionId() || undefined;
    // Fire page_view when user returns to tab after long idle to prevent "(not set)"
    // session attribution in SPAs (GA4 starts a new session after 30 min of inactivity)
    this.setupVisibilityPageView();
  }

  /** Runs the fingerprint and stable-ID setup once analytics is granted in this page. */
  private awaitAnalyticsConsent() {
    if (!this.isWebAppContext) return;
    let off: () => void = () => undefined;
    off = onConsentChange(() => {
      if (!hasAnalyticsConsent() || this.stableVisitorId) return;
      off();
      void (async () => {
        await this.setStableIds();
        await this.setUid();
        this.syncGA4ClientIdToAttribution();
      })();
    });
  }

  /**
   * Get the native GA4 session_id from gtag.
   * This is the numeric session_id that GA4 uses internally to group events into sessions.
   * Needed so server-side Measurement Protocol events (e.g. purchase) can join the same
   * GA4 session as browser-side events (e.g. begin_checkout).
   *
   * Returns a Promise that resolves with the session_id string, or null if unavailable.
   * Always fetches fresh from gtag (sessions rotate after 30 min of inactivity).
   */
  /**
   * GA's native client_id (`<rand>.<ts>`) for server-side MP joins. Reads gtag first,
   * falls back to the `_ga` cookie. Never returns `cid` (a stable-visitor hash that
   * GA4 cannot join); null when GA is blocked/unconsented.
   */
  async getGA4ClientId(): Promise<string | null> {
    const fromGtag = await new Promise<string | null>((resolve) => {
      if (!this.hasGTAG || !this.isWebAppContext) return resolve(null);
      try {
        const timer = setTimeout(() => resolve(null), 3000);
        const gtag = window.gtag;
        if (!gtag) { clearTimeout(timer); return resolve(null); }
        gtag('get', process.env.REACT_APP_GA_ID || 'G-RCEGPEQEZ5', 'client_id', (id: string) => {
          clearTimeout(timer);
          resolve(isValidGaClientId(id) ? id : null);
        });
      } catch {
        resolve(null);
      }
    });
    const resolved = fromGtag ?? (isValidGaClientId(getGaClientId()) ? getGaClientId() : null);
    if (resolved) this.ga4ClientId = resolved;
    return resolved;
  }

  getGA4SessionId(): Promise<string | null> {
    if (!this.hasGTAG || !this.isWebAppContext) return Promise.resolve(null);
    return new Promise((resolve) => {
      try {
        // Timeout after 3s — if gtag hasn't responded, session_id is unavailable
        const timer = setTimeout(() => resolve(null), 3000);
        const gtag = window.gtag;
        if (!gtag) { resolve(null); return; }
        gtag('get', process.env.REACT_APP_GA_ID || 'G-RCEGPEQEZ5', 'session_id', (sessionId: string) => {
          clearTimeout(timer);
          if (sessionId) {
            const id = String(sessionId);
            this.ga4SessionId = id;
            resolve(id);
          } else {
            resolve(null);
          }
        });
      } catch (e) {
        console.warn('[Analytics] Failed to get GA4 session_id:', e);
        resolve(null);
      }
    });
  }

  /**
   * Queue a gtag call or send immediately if gtag is available.
   * In web app context, events are queued until gtag loads (race condition on page load).
   * In extension context, this should not be called — use server-side analytics instead.
   */
  private queueOrSendGtag(type: 'event' | 'set' | 'set_user_properties', ...args: any[]) {
    // Deny by default (web app): no analytics consent means no gtag call and no queueing.
    if (!consentAllows("analytics")) return;
    const gtag = window.gtag;
    if (this.hasGTAG && gtag) {
      if (type === 'set_user_properties') {
        gtag('set', 'user_properties', args[0]);
      } else {
        gtag(type, ...args);
      }
    } else {
      this.gtagEventQueue.push({ type, args });
      console.log(`[Analytics] Queued gtag ${type} (gtag not ready yet):`, args[0]);
    }
  }

  /**
   * Flush queued gtag events. Called after injectPixels() loads GA4.
   * Any events that fired before gtag was ready are now sent.
   */
  flushGtagQueue() {
    if (!this.hasGTAG) {
      console.warn('[Analytics] flushGtagQueue called but gtag still not available');
      return;
    }
    const count = this.gtagEventQueue.length;
    if (count === 0) return;

    console.log(`[Analytics] Flushing ${count} queued gtag events`);
    const gtag = window.gtag;
    if (!gtag) return;
    while (this.gtagEventQueue.length > 0) {
      const { type, args } = this.gtagEventQueue.shift()!;
      // invariant: hasGTAG check at top of method ensures window.gtag is defined
      if (type === 'set_user_properties') {
        gtag('set', 'user_properties', args[0]);
      } else {
        gtag(type, ...args);
      }
    }
  }

  /**
   * Store GA4's native client_id in the attribution cookie so server-side
   * MP events (purchase, extension signup) can match the browser's GA4 user.
   * Uses gtag('get') to fetch the real GA4 client_id from the _ga cookie.
   */
  private syncGA4ClientIdToAttribution() {
    if (!this.isWebAppContext || !this.hasGTAG) return;
    // Reads GA4's client_id (analytics) and writes the attribution cookie (marketing).
    if (!hasAnalyticsConsent() || !hasMarketingConsent()) return;
    const gtag = window.gtag;
    if (!gtag) return;
    try {
      const gaId = process.env.REACT_APP_GA_ID || 'G-RCEGPEQEZ5';
      gtag('get', gaId, 'client_id', (ga4ClientId: string) => {
        if (!ga4ClientId) return;
        try {
          const attribution = readAttributionData();
          if (!attribution) return;

          let changed = false;
          if (attribution.ga_client_id !== ga4ClientId) {
            attribution.ga_client_id = ga4ClientId;
            changed = true;
          }

          const sessionId = getGaSessionId();
          if (sessionId && attribution.ga_session_id !== sessionId) {
            attribution.ga_session_id = sessionId;
            changed = true;
          }

          if (this.stableVisitorId && attribution.stable_visitor_id !== this.stableVisitorId) {
            attribution.stable_visitor_id = this.stableVisitorId;
            changed = true;
          }

          if (!changed) return;

          attribution.last_updated = new Date().toISOString();
          const cookieValue = encodeURIComponent(JSON.stringify(attribution));
          const isLocal = typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
          const cookieSettings = isLocal
            ? 'path=/; max-age=2592000; SameSite=Lax'
            : 'path=/; domain=.blueticks.co; max-age=2592000; SameSite=Lax; Secure';
          document.cookie = `blueticks_attribution=${cookieValue}; ${cookieSettings}`;
          console.log('[Analytics] Synced GA4 client_id:', ga4ClientId, 'session_id:', sessionId);
        } catch (e) {
          // Non-critical
        }
      });
    } catch (e) {
      // Non-critical - don't break analytics init
    }
  }

  /**
   * Fire a page_view when the user returns to the tab after being idle longer
   * than GA4's 30-minute session timeout. In SPAs, returning after idle creates
   * a new GA4 session — without a page_view, the session has no page_location
   * context and shows "(not set)" for source/medium and landing page.
   *
   * Only active in web app context (extension uses server-side analytics).
   */
  private setupVisibilityPageView() {
    if (!this.isWebAppContext || typeof document === 'undefined') return;

    let lastActivityTime = Date.now();

    // Track last activity
    const updateActivity = () => { lastActivityTime = Date.now(); };
    document.addEventListener('click', updateActivity, { passive: true });
    document.addEventListener('keydown', updateActivity, { passive: true });
    document.addEventListener('scroll', updateActivity, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      const idleMs = Date.now() - lastActivityTime;
      const THIRTY_MINUTES = 30 * 60 * 1000;

      if (idleMs >= THIRTY_MINUTES) {
        console.log(`[Analytics] Tab re-focused after ${Math.round(idleMs / 60000)}min idle — firing page_view for new session`);
        this.queueOrSendGtag('event', 'page_view', {
          page_location: window.location.href,
          page_title: document.title,
        });
      }

      updateActivity();
    });
  }

  private isValidUUID(uuid: string): boolean {
    // UUID v4 or v5 regex pattern: 8-4-4-4-12 hex digits with version and variant bits
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[4-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  async setUid() {
    // Store stable visitor ID for internal tracking (VznLytics, MP events, etc.)
    // but do NOT use it as GA4's client_id — GA4 manages its own _ga cookie.
    // Overriding client_id in gtag('config') breaks session_start generation
    // and causes "(not set)" attribution. See docs/attribution/GA4-PROTOCOL-REFERENCE.md
    if (this.stableVisitorId) {
      this.cid = this.stableVisitorId;
    } else {
      this.cid = randomstring.generate(12);
    }

    // Set stable_visitor_id as GA4 user property for cross-platform tracking.
    // This links blueticks.co, app.blueticks.co, and extension MP events
    // without breaking GA4's native session management.
    if (this.isWebAppContext && this.stableVisitorId) {
      this.queueOrSendGtag('set_user_properties', {
        stable_visitor_id: this.stableVisitorId,
      });
    }
  }

  async setStableIds() {
    try {
      // Fingerprinting is non-essential (ePrivacy Art. 5(3)): web app only after analytics consent.
      if (!consentAllows("analytics")) {
        return;
      }

      // Check if fingerprinting is supported in current environment
      if (!isFingerprintSupported()) {
        return;
      }

      // Check if we already have stable IDs in this session
      if (this.stableVisitorId && this.deviceCoreId) {
        return;
      }

      // Generate fresh stable IDs (no persistent storage caching)
      const stableIds = await getStableIds();

      this.stableVisitorId = stableIds.visitorId;
      this.deviceCoreId = stableIds.deviceCoreId;
    } catch (error) {
      console.warn("Failed to initialize stable IDs:", error);
      // Don't throw error, fingerprinting is optional
    }
  }





  identify(user?: UserInterface, subscriptions?: Subscription[]) {
    this.user = user;
    
    // Set user identity in Crisp chat
    crispService.setUserIdentity(user);
    
    // Update subscription data if provided
    if (subscriptions && subscriptions.length > 0) {
      this.updateSubscriptionData(subscriptions);
    }
  }

  /**
   * Updates Crisp user with subscription data
   * @param subscriptions - Array of user subscriptions
   */
  updateSubscriptionData(subscriptions: Subscription[]) {
    if (!subscriptions || subscriptions.length === 0) {
      return;
    }

    // Get the active subscription (prioritize active status)
    const activeSubscription = subscriptions.find(sub => sub.status === 'active') || subscriptions[0];
    
    if (!activeSubscription) {
      return;
    }

    // Extract subscription information
    const planName = activeSubscription.plan_id;
    
    // Handle different subscription types
    let planAmount = 0;
    let planQuantity = 1;
    let unitPrice = 0;
    
    // For Chargebee subscriptions, extract from plan object
    if (activeSubscription.type === 'chargebee' && (activeSubscription as any).plan) {
      const chargebeePlan = (activeSubscription as any).plan;
      planAmount = chargebeePlan.amount || 0;
      planQuantity = chargebeePlan.quantity || 1;
      unitPrice = chargebeePlan.unit_price || 0;
    } else {
      // For other subscription types, try to extract from different fields
      planAmount = (activeSubscription as any).amount || 0;
      planQuantity = parseInt(activeSubscription.quantity ?? '') || 1;
      unitPrice = (activeSubscription as any).unit_price || 0;
    }
    
    // Calculate duration based on plan_id (e.g., "Pro-USD-Monthly" -> "Monthly")
    const duration = planName.includes('Monthly') ? 'Monthly' : 
                    planName.includes('Yearly') ? 'Yearly' : 
                    planName.includes('Weekly') ? 'Weekly' : 'Unknown';
    
    // Find credits from entitlement list
    const entitlementList = (activeSubscription as any).entitlementList;
    const creditsEntitlement = entitlementList?.find((ent: any) => ent.feature_id === 'credits');
    const credits = creditsEntitlement?.value || '0';
    
    // Update Crisp user with subscription data
    crispService.updateSubscriptionInfo({
      planName,
      duration,
      credits,
      planAmount: planAmount / 100, // Convert from cents to dollars
      unitPrice: unitPrice / 100, // Convert from cents to dollars
      quantity: planQuantity,
      status: activeSubscription.status,
      nextBillingAt: activeSubscription.next_billing_at || 0,
      workspaceName: activeSubscription.workspace?.name
    });
  }

  setAuthenticatedUser(user: UserInterface, subscriptions?: Subscription[]) {
    this.userId = user._id;

    if (this.isWebAppContext) {
      // Use gtag('set') to add user_id without reconfiguring GA4.
      // gtag('config') with client_id would reset the session → "(not set)" attribution.
      this.queueOrSendGtag('set', { user_id: user._id });
      console.log(`Set user_id via gtag('set'): ${user._id}`);
    }
    this.identify(user, subscriptions);
    this.setAttributionUserProperties();

    // Track the login event separately
    const signupMethod = user.googleId ? 'google' : 'email';
    const isNewUser = user.createdAt ? moment().diff(moment(user.createdAt), 'minutes') < 5 : false;
    this.event("login", {
      category: "engagement",
      action: "user-login",
      method: "authentication",
      signup_method: signupMethod,
      is_new_user: isNewUser,
    });

    // Meta CompleteRegistration for brand-new accounts (email + Google), consent-gated inside
    // trackFacebookEvent. event_id = reg_<userId> so a server (CAPI) copy can dedup against it.
    if (this.isWebAppContext) {
      let store: KeyValueStore | null = null;
      try { store = window.sessionStorage; } catch { store = null; }
      fireRegistrationOnce({ _id: user._id ?? '', createdAt: user.createdAt ? moment(user.createdAt).toDate() : undefined }, (eventId) => trackSignup(eventId), store);
    }
  }

  /**
   * Set first_touch attribution as GA4 user_properties (user-scoped custom dimensions).
   * This ensures GA4 reports show first_touch_source/medium/campaign at the user level,
   * not just as event params which only populate event-scoped dimensions.
   */
  private setAttributionUserProperties() {
    if (!this.isWebAppContext) return;

    const attribution = readAttributionCookie() ||
      buildAttributionFromUserData(this.user?.analytics_data);
    if (!attribution?.first_touch) return;

    const props: Record<string, string> = {};
    const ft = attribution.first_touch;
    if (ft.utm_source) props.first_touch_source = ft.utm_source;
    if (ft.utm_medium) props.first_touch_medium = ft.utm_medium;
    if (ft.utm_campaign) props.first_touch_campaign = ft.utm_campaign;

    if (Object.keys(props).length > 0) {
      this.queueOrSendGtag('set_user_properties', props);
    }
  }

  clearUserIdentity() {
    this.userId = undefined;

    if (this.isWebAppContext) {
      this.queueOrSendGtag('set', { user_id: null });
      console.log('Cleared user_id via gtag(\'set\')');
    }
    delete this.user;
    
    // Reset Crisp session when user logs out
    crispService.resetSession();
  }

  // Track user engagement score based on frequency, feature breadth, and recency
  trackEngagementScore(score: number, metrics: {
    frequency: number;
    featureBreadth: number;
    recency: number;
    monthlyActiveUser: boolean;
  }) {
    this.event("user_engagement_score", {
      category: "engagement",
      action: "engagement_score_calculated",
      value: score,
      engagement_score: score,
      frequency: metrics.frequency,
      feature_breadth: metrics.featureBreadth,
      recency: metrics.recency,
      is_monthly_active: metrics.monthlyActiveUser,
    });
  }

  // Track account health metrics
  trackAccountHealth(healthScore: number, riskLevel: 'low' | 'medium' | 'high', indicators: Record<string, any>) {
    this.event("account_health", {
      category: "engagement",
      action: "account_health_assessed",
      value: healthScore,
      health_score: healthScore,
      risk_level: riskLevel,
      ...indicators,
    });
  }

  async event(eventName: string, options: EventOptions) {
    const eventNameClean = eventName?.replace(/-/g, '_') ?? '';

    // NOTE: legacy bot_*/kb_* events are intentionally NOT forwarded to the durable
    // `record-agent-event` bus — those events can carry content/labels (PII), and the
    // durable bus is reserved for the explicitly-wired, body-free `agent_*` events
    // (emitted directly via reportAgentEvent at the /agents + bot/KB call sites). The
    // gtag/GA marketing path below handles bot_*/kb_* exactly as before.

    // Route based on context, not gtag availability
    if (this.isWebAppContext) {
      // Web app: use gtag (queue if not ready yet)
      const sendOptions = JSON.parse(JSON.stringify(options));

      const attributionParams = getAttributionParams(eventNameClean, this.user?.analytics_data);
      Object.assign(sendOptions, attributionParams);

      this.queueOrSendGtag("event", eventNameClean, sendOptions);
    } else {
      // Extension context: no client-side gtag, server-side analytics handles tracking
    }

    // Measurement Protocol removed - now backend-only for security
    // Client-side Measurement Protocol calls expose API secrets and allow fake events
    // Server-side tracking is handled by backend services with proper authentication

    {
      const fbq = window.fbq;
      if (this.hasFBQ && fbq && consentAllows("marketing")) {
        sendMetaEvent(fbq, eventName, options);
      }
    }

    const { label, value, category, action, email, ...eventOptions } = options;

    if (consentAllows("analytics")) this.vznLytics.event({
      event_name: eventName,
      label: label,
      category: category,
      sub_category: action,
      value: value,
      email: email,
      data: eventOptions,
      entity_id: this.user?._id,
      entity_type: "user",
      entity_display_name: [this.user?.firstName, this.user?.lastName]
        .filter((x) => x)
        .join(" "),
      analytics_data: this.user?.analytics_data,
    });
  }

  async transaction(transaction: TransactionData, sendTo?: string[]) {
    if (this.isWebAppContext) {
      this.queueOrSendGtag("event", "purchase", Object.assign(transaction, { send_to: "all" }));
    }

    // Measurement Protocol removed - now backend-only for security
    // Client-side Measurement Protocol calls expose API secrets and allow fake events
    // Server-side tracking is handled by backend services with proper authentication

    // Meta Purchase is sent once, by trackFacebookPurchase (with the deduplicable eventID); a second
    // fbq Purchase here double-counted every sale (#855).

    if (consentAllows("analytics")) this.vznLytics.event({
      event_name: "transaction",
      category: "ecommerce",
      value: transaction.value,
      entity_id: this.user?._id,
      entity_type: "user",
      entity_display_name: [this.user?.firstName, this.user?.lastName]
        .filter((x) => x)
        .join(" "),
      analytics_data: this.user?.analytics_data
    });
  }



  async ecommerceEvent(eventName: string, ecommerceData: {
    currency?: string;
    value?: number;
    items: MeasurementProtocolItem[];
    coupon?: string;
    [key: string]: any;
  }, additionalParams?: Record<string, any>) {
    if (this.isWebAppContext) {
      this.queueOrSendGtag('event', eventName, {
        currency: ecommerceData.currency,
        value: ecommerceData.value,
        items: ecommerceData.items,
        coupon: ecommerceData.coupon,
        ...additionalParams
      });
    }

    // Measurement Protocol removed - now backend-only for security
    // Client-side Measurement Protocol calls expose API secrets and allow fake events
    // Server-side tracking is handled by backend services with proper authentication

    if (this.hasFBQ && consentAllows("marketing")) {
      // Facebook Pixel equivalent events
      const fbEventMap: Record<string, string> = {
        'view_item': 'ViewContent',
        'add_to_cart': 'AddToCart',
        'begin_checkout': 'InitiateCheckout',
        'purchase': 'Purchase'
      };
      
      const fbEventName = fbEventMap[eventName];
      const fbq = window.fbq;
      if (fbEventName && fbq) {
        sendMetaEvent(fbq, fbEventName, {
          ...(ecommerceData.value !== undefined && { value: ecommerceData.value }),
          currency: ecommerceData.currency || 'USD',
          content_ids: ecommerceData.items.map(item => item.item_id),
          content_type: 'product'
        });
      }
    }

    if (consentAllows("analytics")) this.vznLytics.event({
      event_name: eventName,
      category: "ecommerce",
      value: ecommerceData.value,
      entity_id: this.user?._id,
      entity_type: "user",
      entity_display_name: [this.user?.firstName, this.user?.lastName]
        .filter((x) => x)
        .join(" "),
      analytics_data: this.user?.analytics_data,
      data: { ...ecommerceData, ...additionalParams }
    });
  }



  /**
   * Generate a deterministic UUID from the current stable visitor ID
   * @param salt Optional salt to add uniqueness for different use cases
   * @param strategy Conversion strategy ('sha256' or 'v5')
   * @returns Promise with deterministic UUID or null if stable visitor ID not available
   */
  async getStableVisitorUuid(salt?: string, strategy: 'sha256' | 'v5' = 'sha256') {
    try {
      if (!this.stableVisitorId) {
        console.warn("No stable visitor ID available for UUID generation");
        return null;
      }

      return await fingerprintToUuid(this.stableVisitorId, { salt, strategy });
    } catch (error) {
      console.warn("Failed to generate UUID from stable visitor ID:", error);
      return null;
    }
  }

  /**
   * Get the stable visitor ID
   */
  getStableVisitorId(): string | undefined {
    return this.stableVisitorId;
  }

  /**
   * Get the device core ID
   */
  getDeviceCoreId(): string | undefined {
    return this.deviceCoreId;
  }

  /**
   * Get the current user ID
   */
  getUserId(): string | undefined {
    return this.userId;
  }

  /**
   * Get the current client ID (GA4 client_id)
   */
  getClientId(): string | undefined {
    return this.cid;
  }
}
